import { supabase } from "../lib/supabase";
import { SupabaseClient } from "@supabase/supabase-js";
import { Booking, Container } from "../types/booking";
import { AGENT_ALL_TAB_BOOKING_LIMIT, AGENT_LIST_BOOKING_STATUSES } from "../config/agentPortal";
import {
  linkBookingToContainer,
  linkMultipleBookingsToContainer,
  updateBookingProduct,
} from "./bookingService";

const typedSupabase = supabase as unknown as SupabaseClient;

/** Full booking graph for detail pages */
const AGENT_BOOKING_SELECT = `
  *,
  transport_type_info:booking_transport_types(*),
  booking_type_info:bookings_type(*),
  company_info:company(
    id,
    name,
    country,
    city,
    street,
    house_number,
    postal_code,
    vat_number,
    eori_number,
    coc_number,
    phone_number,
    invoice_email
  ),
  supplier_info:booking_supplier(*),
  origin_port_info:booking_origin_port(*),
  destination_port_info:booking_destination_port(*),
  booking_incoterm_info:bookings_incoterm(*),
  agent_info:booking_agent(*),
  product_info:booking_product(*),
  container_info:containers(
    *,
    container_origin_port:booking_origin_port(*),
    container_destination_port:booking_destination_port(*),
    master_container_info:master_containers_containers(
      master_container_id,
      master_containers(id, reference, transport_type, agent_id, created_at, status)
    )
  ),
  commercial_invoice_analysis:booking_commercial_invoice_analyses(
    object_path,
    likelihood_score,
    completeness_score,
    proforma_likelihood,
    document_type,
    analyzed_at,
    file_name,
    result
  )
`;

/** Lean payload for bookings list — avoids heavy embeds (CI result JSON, full company, ports). */
const AGENT_BOOKING_LIST_SELECT = `
  id,
  created_at,
  status,
  reference,
  transport_type,
  booking_type,
  service_level,
  company_id,
  container_id,
  agent_id,
  origin_pickup_date,
  origin_received_date,
  confirmed_pick_up_date,
  commercial_invoice_file,
  transport_type_info:booking_transport_types(id, name, abbreviation),
  booking_type_info:bookings_type(id, name),
  company_info:company(id, name),
  product_info:booking_product(id, cbm, gross_weight),
  container_info:containers(id, reference),
  commercial_invoice_analysis:booking_commercial_invoice_analyses(
    object_path,
    likelihood_score,
    completeness_score,
    proforma_likelihood,
    document_type,
    analyzed_at,
    file_name
  )
`;

export const fetchAgentBookings = async (
  agentId: number,
  options?: {
    statusFilter?: string;
    companyId?: number;
    transportTypeId?: number;
    containerId?: number;
    hasContainer?: "yes" | "no";
    bookingTypeId?: number;
    serviceLevel?: "budget" | "premium";
    masterContainerId?: number;
    /** When true, page through every matching row (any active filter). */
    fetchAllMatching?: boolean;
  }
) => {
  try {
    const statusFilter = options?.statusFilter;
    const companyId = options?.companyId;
    const transportTypeId = options?.transportTypeId;
    const containerId = options?.containerId;
    const hasContainer = options?.hasContainer;
    const bookingTypeId = options?.bookingTypeId;
    const serviceLevel = options?.serviceLevel;
    const masterContainerId = options?.masterContainerId;

    let masterContainerIds: number[] | null = null;
    if (masterContainerId != null) {
      const { data: links, error: linksError } = await typedSupabase
        .from("master_containers_containers")
        .select("container_id")
        .eq("master_container_id", masterContainerId);

      if (linksError) throw linksError;
      masterContainerIds = (links || [])
        .map((row) => row.container_id)
        .filter((id): id is number => id != null);

      if (masterContainerIds.length === 0) {
        return { data: [] as Booking[], error: null, truncated: false };
      }
    }

    const useAllTabStatusList = !statusFilter || statusFilter === "all";

    // Only the bare "All" tab uses a capped list. Any filter must page through
    // every matching booking so results are not limited to the preloaded set.
    const fetchAllMatching =
      options?.fetchAllMatching === true ||
      companyId != null ||
      transportTypeId != null ||
      containerId != null ||
      hasContainer != null ||
      bookingTypeId != null ||
      serviceLevel != null ||
      masterContainerId != null ||
      !useAllTabStatusList;

    const batchSize = 1000;
    const allBookings: Booking[] = [];
    let offset = 0;
    const maxRows = fetchAllMatching
      ? Number.POSITIVE_INFINITY
      : AGENT_ALL_TAB_BOOKING_LIMIT;

    while (offset < maxRows) {
      const end = Math.min(offset + batchSize, maxRows) - 1;
      let query = typedSupabase
        .from("bookings")
        .select(AGENT_BOOKING_LIST_SELECT)
        .eq("agent_id", agentId)
        .order("created_at", { ascending: false })
        .range(offset, end);

      if (useAllTabStatusList) {
        query = query.in("status", [...AGENT_LIST_BOOKING_STATUSES]);
      } else if (statusFilter) {
        query = query.eq("status", statusFilter);
      }

      if (companyId != null) {
        query = query.eq("company_id", companyId);
      }

      if (transportTypeId != null) {
        query = query.eq("transport_type", transportTypeId);
      }

      if (containerId != null) {
        query = query.eq("container_id", containerId);
      } else if (masterContainerIds != null) {
        query = query.in("container_id", masterContainerIds);
      } else if (hasContainer === "yes") {
        query = query.not("container_id", "is", null);
      } else if (hasContainer === "no") {
        query = query.is("container_id", null);
      }

      if (bookingTypeId != null) {
        query = query.eq("booking_type", bookingTypeId);
      }

      if (serviceLevel != null) {
        query = query.eq("service_level", serviceLevel);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (data?.length) {
        allBookings.push(...(data as Booking[]));
      }

      if (!data || data.length < batchSize) {
        break;
      }

      offset += batchSize;
    }

    const truncated =
      !fetchAllMatching && allBookings.length >= AGENT_ALL_TAB_BOOKING_LIMIT;

    return { data: allBookings, error: null, truncated };
  } catch (error) {
    console.error("Error fetching agent bookings:", error);
    return { data: [] as Booking[], error, truncated: false };
  }
};

export const fetchAgentBookingStatusCounts = async (agentId: number) => {
  try {
    const byStatus = Object.fromEntries(
      AGENT_LIST_BOOKING_STATUSES.map((status) => [status, 0])
    ) as Record<(typeof AGENT_LIST_BOOKING_STATUSES)[number], number>;

    // Paginate status-only rows (avoids N head-count requests and full booking payloads)
    const batchSize = 1000;
    let offset = 0;
    while (true) {
      const { data, error } = await typedSupabase
        .from("bookings")
        .select("status")
        .eq("agent_id", agentId)
        .in("status", [...AGENT_LIST_BOOKING_STATUSES])
        .range(offset, offset + batchSize - 1);

      if (error) throw error;
      for (const row of data || []) {
        const status = row.status as (typeof AGENT_LIST_BOOKING_STATUSES)[number];
        if (status in byStatus) byStatus[status] += 1;
      }
      if (!data || data.length < batchSize) break;
      offset += batchSize;
    }

    return { data: byStatus, error: null };
  } catch (error) {
    console.error("Error fetching agent booking status counts:", error);
    return {
      data: {} as Record<(typeof AGENT_LIST_BOOKING_STATUSES)[number], number>,
      error,
    };
  }
};

export const fetchAgentBookingCompanyOptions = async (agentId: number) => {
  try {
    const batchSize = 1000;
    const byId = new Map<number, { id: number; name: string }>();
    let offset = 0;

    while (true) {
      const { data, error } = await typedSupabase
        .from("bookings")
        .select("company_id, company_info:company(id, name)")
        .eq("agent_id", agentId)
        .not("company_id", "is", null)
        .range(offset, offset + batchSize - 1);

      if (error) throw error;

      for (const row of data || []) {
        const companyId = row.company_id as number | null;
        if (!companyId) continue;

        const company = Array.isArray(row.company_info)
          ? row.company_info[0]
          : row.company_info;

        if (company?.id && company.name) {
          byId.set(company.id, { id: company.id, name: company.name });
          continue;
        }

        // Fallback when company embed is blocked by RLS (before migration applied)
        if (!byId.has(companyId)) {
          byId.set(companyId, {
            id: companyId,
            name: `Company #${companyId}`,
          });
        }
      }

      if (!data || data.length < batchSize) break;
      offset += batchSize;
    }

    const companies = [...byId.values()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    return { data: companies, error: null };
  } catch (error) {
    console.error("Error fetching agent booking companies:", error);
    return { data: [] as { id: number; name: string }[], error };
  }
};

export interface AgentCompanyContactEmail {
  email: string;
  source: "user" | "invoice";
}

/** Emails for a company linked to an agent booking (users + invoice email). */
export const fetchAgentCompanyContactEmails = async (
  agentId: number,
  companyId: number
): Promise<{ data: AgentCompanyContactEmail[]; error: unknown }> => {
  try {
    const { data: link, error: linkError } = await typedSupabase
      .from("bookings")
      .select("id")
      .eq("agent_id", agentId)
      .eq("company_id", companyId)
      .limit(1)
      .maybeSingle();

    if (linkError) throw linkError;
    if (!link) return { data: [], error: null };

    const { data: company, error: companyError } = await typedSupabase
      .from("company")
      .select("invoice_email")
      .eq("id", companyId)
      .maybeSingle();

    if (companyError) throw companyError;

    const emails: AgentCompanyContactEmail[] = [];
    const seen = new Set<string>();

    const addEmail = (email: string | null | undefined, source: "user" | "invoice") => {
      const normalized = email?.trim().toLowerCase();
      if (
        !normalized ||
        normalized === "info@efreightforwarding.com" ||
        seen.has(normalized)
      ) {
        return;
      }
      seen.add(normalized);
      emails.push({ email: email!.trim(), source });
    };

    addEmail(company?.invoice_email, "invoice");

    const { data: userCompanies, error: ucError } = await typedSupabase
      .from("user_companies")
      .select("user_id")
      .eq("company_id", companyId);

    if (!ucError && userCompanies?.length) {
      const { data: sessionData } = await typedSupabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (token) {
        for (const row of userCompanies) {
          if (!row.user_id) continue;
          try {
            const { data: userData, error: userError } =
              await typedSupabase.functions.invoke("admin-users/get-user", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: { userId: row.user_id },
              });
            if (!userError && userData?.user?.email) {
              addEmail(userData.user.email, "user");
            }
          } catch {
            // Skip users we cannot resolve
          }
        }
      }
    }

    return { data: emails, error: null };
  } catch (error) {
    console.error("Error fetching agent company emails:", error);
    return { data: [], error };
  }
};

export const fetchAgentBookingById = async (
  agentId: number,
  bookingId: number
) => {
  try {
    const { data, error } = await typedSupabase
      .from("bookings")
      .select(AGENT_BOOKING_SELECT)
      .eq("id", bookingId)
      .eq("agent_id", agentId)
      .maybeSingle();

    if (error) throw error;
    return { data: data as Booking | null, error: null };
  } catch (error) {
    console.error("Error fetching agent booking:", error);
    return { data: null, error };
  }
};

export const updateAgentBooking = async (
  agentId: number,
  bookingId: number,
  updates: Record<string, unknown>
) => {
  try {
    const { data, error } = await typedSupabase
      .from("bookings")
      .update(updates)
      .eq("id", bookingId)
      .eq("agent_id", agentId)
      .select(AGENT_BOOKING_SELECT)
      .single();

    if (error) throw error;
    return { data: data as Booking, error: null };
  } catch (error) {
    console.error("Error updating agent booking:", error);
    return { data: null, error };
  }
};

export const deconsolidateAgentBooking = async (
  agentId: number,
  bookingId: number
) => {
  return updateAgentBooking(agentId, bookingId, { container_id: null });
};

export const fetchAgentContainers = async () => {
  try {
    const batchSize = 1000;
    const allContainers: Record<string, unknown>[] = [];
    let offset = 0;

    while (true) {
      const { data, error } = await typedSupabase
        .from("containers")
        .select(
          `
          id,
          created_at,
          status,
          reference,
          transport_type,
          origin_port,
          destination_port,
          etd,
          delayed_etd,
          eta,
          track_and_trace,
          transport_type_info:booking_transport_types(id, name),
          origin_port_info:booking_origin_port(id, name),
          destination_port_info:booking_destination_port(id, name),
          master_container_info:master_containers_containers(
            master_container_id,
            master_containers(id, reference, transport_type, agent_id, created_at)
          )
        `
        )
        .order("created_at", { ascending: false })
        .range(offset, offset + batchSize - 1);

      if (error) throw error;
      if (data?.length) allContainers.push(...data);
      if (!data || data.length < batchSize) break;
      offset += batchSize;
    }

    return { data: allContainers, error: null };
  } catch (error) {
    console.error("Error fetching agent containers:", error);
    return { data: [], error };
  }
};

export interface AgentContainerOption {
  id: number;
  reference: string | null;
  status: string | null;
  transport_type_info?: { name: string } | { name: string }[];
}

/** Lightweight list of all containers for consolidation dropdowns. */
export const fetchAgentContainerOptions = async () => {
  try {
    const batchSize = 1000;
    const allContainers: AgentContainerOption[] = [];
    let offset = 0;

    while (true) {
      const { data, error } = await typedSupabase
        .from("containers")
        .select(
          `
          id,
          reference,
          status,
          transport_type_info:booking_transport_types(name)
        `
        )
        .order("reference", { ascending: true, nullsFirst: false })
        .range(offset, offset + batchSize - 1);

      if (error) throw error;
      if (data?.length) {
        allContainers.push(...(data as AgentContainerOption[]));
      }
      if (!data || data.length < batchSize) break;
      offset += batchSize;
    }

    return { data: allContainers, error: null };
  } catch (error) {
    console.error("Error fetching container options:", error);
    return { data: [] as AgentContainerOption[], error };
  }
};

export const fetchAgentContainerById = async (containerId: number) => {
  try {
    const { data, error } = await typedSupabase
      .from("containers")
      .select(
        `
        *,
        transport_type_info:booking_transport_types(*),
        origin_port_info:booking_origin_port(*),
        destination_port_info:booking_destination_port(*),
        master_container_info:master_containers_containers(
          master_container_id,
          master_containers(id, reference, transport_type, agent_id, created_at)
        )
      `
      )
      .eq("id", containerId)
      .maybeSingle();

    if (error) throw error;
    return { data: data as Container | null, error: null };
  } catch (error) {
    console.error("Error fetching agent container:", error);
    return { data: null, error };
  }
};

export const fetchContainerBookingsForAgent = async (
  agentId: number,
  containerId: number
) => {
  try {
    const { data, error } = await typedSupabase
      .from("bookings")
      .select(AGENT_BOOKING_SELECT)
      .eq("container_id", containerId)
      .eq("agent_id", agentId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return { data: (data || []) as Booking[], error: null };
  } catch (error) {
    console.error("Error fetching container bookings for agent:", error);
    return { data: [] as Booking[], error };
  }
};

export const createAgentContainer = async (values: {
  reference: string;
  transport_type: number;
  etd?: string | null;
  delayed_etd?: string | null;
  eta?: string | null;
  eta_customer?: string | null;
  track_and_trace?: string | null;
  origin_port?: number | null;
  destination_port?: number | null;
}) => {
  try {
    const { data, error } = await typedSupabase
      .from("containers")
      .insert([{ ...values, status: "planned" }])
      .select()
      .single();

    if (error) throw error;
    return { data: data as Container, error: null };
  } catch (error) {
    console.error("Error creating container:", error);
    return { data: null, error };
  }
};

export const updateAgentContainer = async (
  containerId: number,
  values: Record<string, unknown>
) => {
  try {
    const { data, error } = await typedSupabase
      .from("containers")
      .update(values)
      .eq("id", containerId)
      .select()
      .single();

    if (error) throw error;
    return { data: data as Container, error: null };
  } catch (error) {
    console.error("Error updating container:", error);
    return { data: null, error };
  }
};

export const findContainerByReference = async (reference: string) => {
  try {
    const { data, error } = await typedSupabase
      .from("containers")
      .select("id, reference, status")
      .eq("reference", reference.trim())
      .maybeSingle();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Error finding container:", error);
    return { data: null, error };
  }
};

export {
  linkBookingToContainer,
  linkMultipleBookingsToContainer,
  updateBookingProduct,
};
